import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import NotificationCenter from './components/NotificationCenter';
import BatteryLowPopup from './components/BatteryLowPopup';
import RobotOfflinePopup from './components/RobotOfflinePopup';
import BatteryLowNotification from './components/BatteryLowNotification';

// Auth pages - Keep these synchronous as they're needed immediately
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import AcceptInvitation from './pages/auth/AcceptInvitation';

// Lazy load heavy components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const LandingPage = lazy(() => import('./pages/apps/LandingPage'));
const RobotControl = lazy(() => import('./pages/apps/RobotControl'));
const HistoryExplorer = lazy(() => import('./pages/apps/HistoryExplorer'));
const CommandControl = lazy(() => import('./pages/apps/CommandControl'));
const Tracker = lazy(() => import('./pages/apps/Tracker'));
const Schedule = lazy(() => import('./pages/apps/Schedule'));
const RealRobotTracker = lazy(() => import('./components/RealRobotTracker'));
const Configurator = lazy(() => import('./pages/apps/Configurator'));
const RobotMappingManager = lazy(() => import('./pages/apps/RobotMappingManager'));
const SiteMappingManager = lazy(() => import('./pages/apps/SiteMappingManager'));
const HardwareAssetManager = lazy(() => import('./pages/apps/HardwareAssetManager'));
const OrganizationRobotMappingManager = lazy(() => import('./pages/apps/OrganizationRobotMappingManager'));
const SupervisorMappingManager = lazy(() => import('./pages/apps/SupervisorMappingManager'));
const RobotManager = lazy(() => import('./pages/apps/RobotManager'));
const CustomerManager = lazy(() => import('./pages/apps/CustomerManager'));
const OrganizationManager = lazy(() => import('./pages/apps/OrganizationManager'));
const UserManager = lazy(() => import('./pages/apps/UserManager'));
const InvitationManager = lazy(() => import('./pages/apps/InvitationManager'));
const OrganizationTeamManager = lazy(() => import('./pages/apps/OrganizationTeamManager'));
const ContractManagement = lazy(() => import('./pages/apps/ContractManagement'));
const EmqxConnections = lazy(() => import('./pages/apps/EmqxConnections'));
const GpioMonitorPage = lazy(() => import('./pages/apps/GpioMonitorPage'));
const ReportingDashboard = lazy(() => import('./pages/apps/ReportingDashboard'));
const EventReport = lazy(() => import('./pages/apps/EventReport'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Notifications = lazy(() => import('./pages/Notifications'));
const MissionMapManager = lazy(() => import('./pages/apps/MissionMapManager'));
const TrackingHistory = lazy(() => import('./pages/apps/TrackingHistory'));
const NoAccess = lazy(() => import('./pages/NoAccess'));



const ORG_TEAM_ROLES = ['ORG_ADMIN', 'SITE_OPERATIONS_MANAGER'];
const ORG_FLEET_ROLES = ['ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR'];
const LANDING_PAGE_ROLES = ['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'TELE_OPERATOR'];
const COMMAND_CONTROL_ROLES = ['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'TELE_OPERATOR'];

function InvitationManagerRoute() {
  const { user } = useAuth();
  if (user && ORG_TEAM_ROLES.includes(user.role)) {
    return <Navigate to="/organization-team?tab=invitations" replace />;
  }
  if (user && ['SUPER_ADMIN', 'ADMIN'].includes(user.role)) {
    return <Navigate to="/organization-manager" replace />;
  }
  return (
    <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
      <InvitationManager />
    </ProtectedRoute>
  );
}

function OrganizationUsersRoute() {
  const { user } = useAuth();
  if (user && ORG_TEAM_ROLES.includes(user.role)) {
    return <Navigate to="/organization-team?tab=users" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const { loading, isAuthenticated, user } = useAuth();

  // Helper function to get the appropriate redirect path based on user role
  const getRedirectPath = () => {
    if (!user) return '/dashboard';
    return user.role === 'CUSTOMER' ? '/tracker' : '/dashboard';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Notification Center */}
      {isAuthenticated && user?.notificationsEnabled !== false && <NotificationCenter />}
      
      {/* Battery / offline popups — only when notifications are enabled */}
      {isAuthenticated && user?.notificationsEnabled !== false && <BatteryLowPopup />}
      
      {isAuthenticated && user?.notificationsEnabled !== false && <RobotOfflinePopup />}
      
      {isAuthenticated && user?.notificationsEnabled !== false && <BatteryLowNotification />}
      
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to={getRedirectPath()} /> : <Login />} 
        />
        <Route 
          path="/signup" 
          element={isAuthenticated ? <Navigate to={getRedirectPath()} /> : <Signup />} 
        />
        <Route 
          path="/forgot-password" 
          element={isAuthenticated ? <Navigate to={getRedirectPath()} /> : <ForgotPassword />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
        <Route 
          path="/accept-invitation" 
          element={isAuthenticated ? <Navigate to={getRedirectPath()} /> : <AcceptInvitation />} 
        />

        {/* Protected routes */}
        <Route path="/" element={<Navigate to={getRedirectPath()} />} />
        
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/emqx" 
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
              <EmqxConnections />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/landing-page" 
          element={
            <ProtectedRoute
              requiredRoles={LANDING_PAGE_ROLES}
              requiredPermission="landingPage"
              permissionLevel="RO"
            >
              <LandingPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/apps/landing" 
          element={
            <ProtectedRoute
              requiredRoles={LANDING_PAGE_ROLES}
              requiredPermission="landingPage"
              permissionLevel="RO"
            >
              <LandingPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/apps/robot-control/:robotId" 
          element={
            <ProtectedRoute requiredRoles={LANDING_PAGE_ROLES}>
              <RobotControl />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/apps/robot-control/:robotId/history" 
          element={
            <ProtectedRoute requiredRoles={LANDING_PAGE_ROLES}>
              <HistoryExplorer />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/command-control" 
          element={
            <ProtectedRoute
              requiredRoles={COMMAND_CONTROL_ROLES}
              requiredPermission="commandControl"
              permissionLevel="RO"
            >
              <CommandControl />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/tracker" 
          element={
            <ProtectedRoute requiredRoles={['CUSTOMER', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR', 'ADMIN', 'SUPER_ADMIN', 'SUPERVISOR']}>
              <Tracker />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/reporting" 
          element={
            <ProtectedRoute requiredRoles={['CUSTOMER', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR', 'ADMIN', 'SUPER_ADMIN']}>
              <ReportingDashboard />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/reporting/event/:eventId" 
          element={
            <ProtectedRoute requiredRoles={['CUSTOMER', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR', 'ADMIN', 'SUPER_ADMIN']}>
              <EventReport />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/schedule" 
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'ORG_ADMIN', 'TELE_OPERATOR']}>
              <Schedule />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/real-tracker" 
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'ORG_ADMIN', 'TELE_OPERATOR']}>
              <RealRobotTracker />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/gpio-monitor" 
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'ORG_ADMIN', 'TELE_OPERATOR']}>
              <GpioMonitorPage />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/configurator" 
          element={
            <ProtectedRoute
              requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'TELE_OPERATOR']}
              requiredPermission="configurator"
              permissionLevel="RO"
            >
              <Configurator />
            </ProtectedRoute>
          } 
        />

      <Route
        path="/robot-mappings"
        element={
          <ProtectedRoute
            requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'ORG_ADMIN', 'TELE_OPERATOR']}
            requiredPermission="configurator"
            permissionLevel="RO"
          >
            <RobotMappingManager />
          </ProtectedRoute>
        }
      />

        <Route 
          path="/site-mappings"
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
              <SiteMappingManager />
            </ProtectedRoute>
          }
        />

        <Route
          path="/hardware-assets"
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
              <HardwareAssetManager />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/organization-robot-mappings"
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
              <OrganizationRobotMappingManager />
            </ProtectedRoute>
          }
        />

      <Route
        path="/robot-manager"
        element={
          <ProtectedRoute
            requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'TELE_OPERATOR']}
            requiredPermission="configurator"
            permissionLevel="RO"
          >
            <RobotManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/customer-manager"
        element={
          <ProtectedRoute
            requiredRoles={['SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'CUSTOMER', 'ORG_ADMIN', 'TELE_OPERATOR']}
            requiredPermission="configurator"
            permissionLevel="RO"
          >
            <CustomerManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/organization-manager/*"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR']}>
            <OrganizationManager />
          </ProtectedRoute>
        }
      />

      <Route
        path="/org-setup"
        element={
          <ProtectedRoute requiredRoles={["SUPER_ADMIN"]}>
            <Navigate to="/organization-manager/setup" replace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
            <UserManager />
          </ProtectedRoute>
        }
      />

      <Route path="/invitation-manager" element={<InvitationManagerRoute />} />

      <Route
        path="/organization-team"
        element={
          <ProtectedRoute requiredRoles={['ORG_ADMIN', 'SITE_OPERATIONS_MANAGER']}>
            <OrganizationTeamManager />
          </ProtectedRoute>
        }
      />

      <Route path="/organization-users" element={<OrganizationUsersRoute />} />

      <Route
        path="/contract-management"
        element={
          <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER']}>
            <ContractManagement />
          </ProtectedRoute>
        }
      />

      <Route
        path="/supervisor-mappings"
        element={
          <ProtectedRoute requiredRoles={["SUPER_ADMIN", "ADMIN"]}>
            <SupervisorMappingManager />
          </ProtectedRoute>
        }
      />

        <Route
          path="/mission-maps"
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN', 'ORG_ADMIN', 'SITE_OPERATIONS_MANAGER', 'FIELD_OPERATOR']}>
              <MissionMapManager />
            </ProtectedRoute>
          }
        />

        <Route
          path="/tracking-history"
          element={
            <ProtectedRoute requiredRoles={['SUPER_ADMIN', 'ADMIN']}>
              <TrackingHistory />
            </ProtectedRoute>
          }
        />

        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/no-access" 
          element={<NoAccess />} 
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to={getRedirectPath()} />} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default App;
